// src/components/Sidebar.jsx
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Divider,
  Tooltip,
  Badge,
  Collapse,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GppGoodIcon from "@mui/icons-material/GppGood";
import PeopleIcon from "@mui/icons-material/People";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import CurrencyBitcoinIcon from "@mui/icons-material/CurrencyBitcoin";
import WebIcon from "@mui/icons-material/Web";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CommentIcon from "@mui/icons-material/Comment";
import { getDashboardCounts } from "../services/api";
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CelebrationIcon from '@mui/icons-material/Celebration';
const drawerWidth = 260;
const compactWidth = 72;

const navItems = [
  { to: "/", label: "Dashboard", icon: <DashboardIcon /> },
  { to: "/deposits", label: "Deposits", icon: <AccountBalanceWalletIcon />, badgeKey: "deposits" },
  { to: "/referrals", label: "Referrals", icon: <GroupAddIcon />, badgeKey: "deposits" },
  { to: "/withdrawals", label: "Withdrawals", icon: <MoneyOffIcon />, badgeKey: "withdrawals" },
  // { to: "/transactions", label: "Transactions", icon: <SwapHorizIcon />, badgeKey: "transactions" },
  { to: "/kyc", label: "KYC", icon: <GppGoodIcon />, badgeKey: "kyc" },
  // { to: "/assets", label: "Crypto Assets", icon: <CurrencyBitcoinIcon /> },
];

export default function Sidebar({ initialCompact = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [compact, setCompact] = useState(initialCompact);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [siteSettingsOpen, setSiteSettingsOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [websiteSettingsOpen, setWebsiteSettingsOpen] = useState(false);
  const [websitePackagesOpen, setWebsitePackagesOpen] = useState(false);
  const [profitsOpen, setProfitsOpen] = useState(false);
  const [websiteBlogsOpen, setWebsiteBlogsOpen] = useState(false);

  // FAQ collapse state
  const [websiteFaqOpen, setWebsiteFaqOpen] = useState(false);
  // Testimonials collapse state (NEW & separate)
  const [websiteTestimonialsOpen, setWebsiteTestimonialsOpen] = useState(false);

  const [counts, setCounts] = useState({
    deposits: 0,
    withdrawals: 0,
    transactions: 0,
    kyc: 0,
  });

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const res = await getDashboardCounts();
        setCounts(res.data?.data || {});
      } catch (err) {
        console.error("Failed to fetch counts", err);
      }
    };
    loadCounts();
  }, []);

  const toggleCompact = () => setCompact((s) => !s);

  const onLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/login");
  };

  return (
    <Drawer
      variant="permanent"
      open
      sx={{
        width: compact ? compactWidth : drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        "& .MuiDrawer-paper": {
          width: compact ? compactWidth : drawerWidth,
          boxSizing: "border-box",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: "hidden",
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
          justifyContent: compact ? "center" : "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          <Avatar sx={{ bgcolor: "primary.main" }}>R</Avatar>
          {!compact && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Rockstar
              </Typography>
            </Box>
          )}
        </Box>

        <Tooltip title={compact ? "Expand" : "Collapse"}>
          <IconButton onClick={toggleCompact} size="small" aria-label={compact ? "Expand sidebar" : "Collapse sidebar"}>
            {compact ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      <Divider />

      <Box sx={{ mt: 1, px: compact ? 0.5 : 1 }}>
        <List dense>
          {navItems.map((it) => {
            const badgeCount = it.badgeKey ? counts[it.badgeKey] || 0 : 0;

            return (
              <Box key={it.to}>
                <ListItemButton
                  component={NavLink}
                  to={it.to}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    px: compact ? 1.25 : 1.5,
                    justifyContent: compact ? "center" : "flex-start",
                    "&.active": {
                      bgcolor: "action.selected",
                      boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                    },
                    transition: theme.transitions.create(["background-color", "box-shadow"], {
                      duration: theme.transitions.duration.short,
                    }),
                  }}
                  title={compact ? it.label : undefined}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: compact ? 0 : 1.5, justifyContent: "center", color: "primary.main" }}>
                    {badgeCount > 0 ? <Badge badgeContent={badgeCount} color="error">{it.icon}</Badge> : it.icon}
                  </ListItemIcon>

                  {!compact && <ListItemText primary={it.label} />}
                </ListItemButton>
              </Box>
            );
          })}
          {/* Logs Button */}
          <Box sx={{ mt: 1 }}>
            <ListItemButton
              component={NavLink}
              to="/logs"
              sx={{
                borderRadius: 1,
                mb: 0.5,
                px: compact ? 1.25 : 1.5,
                justifyContent: compact ? "center" : "flex-start",
                "&.active": {
                  bgcolor: "action.selected",
                  boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
                },
              }}
              title={compact ? "Logs" : undefined}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: compact ? 0 : 1.5,
                  justifyContent: "center",
                  color: "primary.main",
                }}
              >
                <ListAltIcon />
              </ListItemIcon>

              {!compact && <ListItemText primary="Logs" />}
            </ListItemButton>
          </Box>

          <Box sx={{ mt: 1 }}>
            <ListItemButton
              onClick={() => setProfitsOpen((s) => !s)}
              sx={{ borderRadius: 1, px: compact ? 1.25 : 1.5, justifyContent: compact ? "center" : "flex-start" }}
            >
              <ListItemIcon sx={{ minWidth: 0, mr: compact ? 0 : 1.5, justifyContent: "center" }}>
                <MonetizationOnIcon />
              </ListItemIcon>

              {!compact && (
                <>
                  <ListItemText primary="Profits" />
                  {profitsOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>

            <Collapse in={profitsOpen && !compact} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton component={NavLink} to="/profits" sx={{ pl: 6, py: 0.75, borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <VisibilityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="View Profits" />
                </ListItemButton>

                <ListItemButton component={NavLink} to="/profits/add" sx={{ pl: 6, py: 0.75, borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <AddCircleOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Add Profit" />
                </ListItemButton>
              </List>
            </Collapse>
          </Box>

          {/* Users collapse */}
          <Box sx={{ mt: 1 }}>
            <ListItemButton onClick={() => setUsersOpen((s) => !s)} sx={{ borderRadius: 1, px: compact ? 1.25 : 1.5, justifyContent: compact ? "center" : "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 0, mr: compact ? 0 : 1.5, justifyContent: "center" }}>
                <PeopleIcon />
              </ListItemIcon>

              {!compact && (
                <>
                  <ListItemText primary="Users" />
                  {usersOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>

            <Collapse in={usersOpen && !compact} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton component={NavLink} to="/users" sx={{ pl: 6, py: 0.75, borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <VisibilityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="View Users" />
                </ListItemButton>

                <ListItemButton component={NavLink} to="/users/add" sx={{ pl: 6, py: 0.75, borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <PersonAddIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Add User" />
                </ListItemButton>
              </List>
            </Collapse>
          </Box>

          {/* Settings */}
          <Box sx={{ mt: 1 }}>
            <ListItemButton onClick={() => setSettingsOpen((s) => !s)} sx={{ borderRadius: 1, px: compact ? 1.25 : 1.5, justifyContent: compact ? "center" : "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 0, mr: compact ? 0 : 1.5, justifyContent: "center" }}>
                <SettingsIcon />
              </ListItemIcon>
              {!compact && (
                <>
                  <ListItemText primary="Admin Settings" />
                  {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>

            <Collapse in={settingsOpen && !compact} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton component={NavLink} to="/settings/profile" sx={{ pl: 6, py: 0.75, borderRadius: 1 }}>
                  <ListItemText primary="Profile" />
                </ListItemButton>

                <ListItemButton component={NavLink} to="/settings/security" sx={{ pl: 6, py: 0.75, borderRadius: 1 }}>
                  <ListItemText primary="Security" />
                </ListItemButton>

              </List>
            </Collapse>
          </Box>

          {/* Website Settings */}
          <Box sx={{ mt: 1 }}>
            <ListItemButton onClick={() => setWebsiteSettingsOpen((s) => !s)} sx={{ borderRadius: 1, px: compact ? 1.25 : 1.5, justifyContent: compact ? "center" : "flex-start" }}>
              <ListItemIcon sx={{ minWidth: 0, mr: compact ? 0 : 1.5, justifyContent: "center" }}>
                <WebIcon />
              </ListItemIcon>
              {!compact && (
                <>
                  <ListItemText primary="Website Settings" />
                  {websiteSettingsOpen ? <ExpandLess /> : <ExpandMore />}
                </>
              )}
            </ListItemButton>

            <Collapse in={websiteSettingsOpen && !compact} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* General (non-collapsible) Settings */}
<ListItemButton
  component={NavLink}
  to="/website-settings/general"
  sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}
>
  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
    <SettingsIcon fontSize="small" />
  </ListItemIcon>
  {!compact ? <ListItemText primary="General Settings" /> : <Tooltip title="General Settings"><SettingsIcon fontSize="small" /></Tooltip>}
</ListItemButton>
<ListItemButton
  component={NavLink}
  to="/website-settings/festival-greetings"
  sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}
>
  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
    <CelebrationIcon fontSize="small" />
  </ListItemIcon>
  {!compact ? (
    <ListItemText primary="Greetings" />
  ) : (
    <Tooltip title="Greetings">
      <CelebrationIcon fontSize="small" />
    </Tooltip>
  )}
</ListItemButton>
<ListItemButton
  component={NavLink}
  to="/website-settings/banner"
  sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}
>
  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
    <SettingsIcon fontSize="small" />
  </ListItemIcon>
  {!compact ? <ListItemText primary="Banners" /> : <Tooltip title="Banners"><SettingsIcon fontSize="small" /></Tooltip>}
</ListItemButton>
                {/* Site Settings collapsible inside */}
                <ListItemButton onClick={() => setSiteSettingsOpen((s) => !s)} sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Site Settings" />
                  {siteSettingsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={siteSettingsOpen} timeout="auto" unmountOnExit>
                  <ListItemButton component={NavLink} to="/website-settings/logos" sx={{ pl: 10, py: 0.75 }}>
                    <ListItemText primary="Logos" />
                  </ListItemButton>

                  <ListItemButton component={NavLink} to="/website-settings/legal-pages" sx={{ pl: 10, py: 0.75 }}>
                    <ListItemText primary="Legal Pages" />
                  </ListItemButton>

                  <ListItemButton component={NavLink} to="/website-settings/footer-settings" sx={{ pl: 10, py: 0.75 }}>
                    <ListItemText primary="Footer" />
                  </ListItemButton>
                </Collapse>

                {/* Packages inside Website Settings */}
                <ListItemButton onClick={() => setWebsitePackagesOpen((s) => !s)} sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <Inventory2Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Packages" />
                  {websitePackagesOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={websitePackagesOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/website-settings/packages" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <ListAltIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="View Packages" />
                    </ListItemButton>

                    <ListItemButton component={NavLink} to="/website-settings/packages/add" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <AddBoxIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Add Package" />
                    </ListItemButton>
                    <ListItemButton component={NavLink} to="/website-settings/telegram-packages/add" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <AddBoxIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Add Telegram Package" />
                    </ListItemButton>
                  </List>
                </Collapse>

                {/* FAQ inside Website Settings */}
                <ListItemButton onClick={() => setWebsiteFaqOpen((s) => !s)} sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <ListAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="FAQ" />
                  {websiteFaqOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={websiteFaqOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/website-settings/faq/categories/add" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <AddBoxIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Add Category" />
                    </ListItemButton>

                    <ListItemButton component={NavLink} to="/website-settings/faq" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <ListAltIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="View FAQs" />
                    </ListItemButton>

                    <ListItemButton component={NavLink} to="/website-settings/faq/add" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <AddCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Add FAQ" />
                    </ListItemButton>
                  </List>
                </Collapse>

                {/* Testimonials inside Website Settings */}
                <ListItemButton onClick={() => setWebsiteTestimonialsOpen((s) => !s)} sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                    <CommentIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Testimonials" />
                  {websiteTestimonialsOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={websiteTestimonialsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    <ListItemButton component={NavLink} to="/website-settings/testimonials" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <ListAltIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="View Testimonials" />
                    </ListItemButton>

                    <ListItemButton component={NavLink} to="/website-settings/testimonial/add" sx={{ pl: 10, py: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
                        <AddCircleOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Add Testimonial" />
                    </ListItemButton>
                  </List>
                </Collapse>
                {/* Blogs inside Website Settings */}
<ListItemButton
  onClick={() => setWebsiteBlogsOpen((s) => !s)}
  sx={{ pl: 6, py: 0.75, borderRadius: 1, mt: 0.5 }}
>
  <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
    <ListAltIcon fontSize="small" />
  </ListItemIcon>
  <ListItemText primary="Blogs" />
  {websiteBlogsOpen ? <ExpandLess /> : <ExpandMore />}
</ListItemButton>

<Collapse in={websiteBlogsOpen && !compact} timeout="auto" unmountOnExit>
  <List component="div" disablePadding>
    <ListItemButton
      component={NavLink}
      to="/website-settings/blogs/categories/add"
      sx={{ pl: 10, py: 0.5, borderRadius: 1 }}
    >
      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
        <AddBoxIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary="Add Category" />
    </ListItemButton>

    <ListItemButton
      component={NavLink}
      to="/website-settings/blogs/add"
      sx={{ pl: 10, py: 0.5, borderRadius: 1 }}
    >
      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
        <AddCircleOutlineIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary="Add Blog" />
    </ListItemButton>

    <ListItemButton
      component={NavLink}
      to="/website-settings/blogs"
      sx={{ pl: 10, py: 0.5, borderRadius: 1 }}
    >
      <ListItemIcon sx={{ minWidth: 0, mr: 1.5, justifyContent: "center" }}>
        <ListAltIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary="View Blogs" />
    </ListItemButton>
  </List>
</Collapse>


              </List>
            </Collapse>
          </Box>
        </List>
      </Box>

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ p: compact ? 1 : 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: compact ? "center" : "space-between", gap: 1 }}>
          <Tooltip title="Settings">
            <IconButton size="small" onClick={() => navigate("/settings")}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {!compact && <Typography variant="caption" color="text.secondary">v1.0.0 • Secure & Compliant</Typography>}

          <Tooltip title={compact ? "Expand" : "Toggle Menu"}>
            <IconButton size="small" onClick={toggleCompact}>
              <MenuIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
}
